import { type ButtonProps as HeroUiButtonProps, Button as HeroUiButton, extendVariants } from '@heroui/react';
import clsx from 'clsx';
import { type ReactElement, forwardRef, memo, type Ref } from 'react';
import { twMerge } from 'tailwind-merge';

// Define the colors that are supported when using the `solid` variant
type SolidColors = {
    variant?: 'solid' | undefined;
    color?:
        | 'black'
        | 'green'
        | 'white'
        | 'primary'
        | 'purple'
        | 'red'
        | 'primary-gradient'
        | 'purple-gradient'
        | 'slate-grey'
        | 'default';
};

// Define the colors that are supported when using the `bordered` and `bordered-white` variants
type BorderedColors = {
    variant: 'bordered' | 'bordered-white' | 'bordered-white-light';
    color: 'slate-grey' | 'primary' | 'transparent' | 'black';
};

// Define the colors that are supported when using the `light` variant
type LightColors = {
    variant: 'light';
    color?: 'slate-grey';
};

// Define the colors that are supported when using the `solid-highlight` variant
type SolidHighlightColors = {
    variant: 'solid-highlight';
    color?: 'slate-grey';
};

type ValidVariantColorCombinations = SolidColors | BorderedColors | LightColors | SolidHighlightColors;

// For icon-only buttons, require aria-label, because otherwise screen readers
// have no text to go off
type IconOnlyButton = {
    isIconOnly: true;
    'aria-label': string;
};
type RegularButton = {
    isIconOnly?: false;
    'aria-label'?: string;
};
type ValidAccessibilityProps = IconOnlyButton | RegularButton;

type HeightOption = 6.25 | 8 | 8.5 | 9 | 12.5 | 18;

type ButtonProps = {
    children: React.ReactNode;
    type?: HeroUiButtonProps['type'];
    onPress?: HeroUiButtonProps['onPress'];
    className?: string;
    round?: boolean;
    highlight?: boolean;
    fontWeight?: 'semibold' | 'normal';

    // We have to use different properties than height and radius since our types don't
    // match the ones in HeroUiButton
    height?: HeightOption;
    tabletHeight?: HeightOption;
    desktopHeight?: HeightOption;
    radius?: 'full' | 2 | 5;
    isDisabled?: HeroUiButtonProps['isDisabled'];
    fullWidth?: HeroUiButtonProps['fullWidth'];
    icon?: React.ReactNode;
} & ValidVariantColorCombinations &
    ValidAccessibilityProps;

const fromPurple = 'from-purple-500-alt';
const toPurple = 'to-purple-450-alt';

const ExtendedButton = extendVariants(HeroUiButton, {
    compoundVariants: [
        // solid colors
        { variant: 'solid', color: 'slate-grey', className: 'bg-slate-grey-200 text-black' },
        { variant: 'solid', color: 'black', className: 'bg-black text-white disabled:bg-slate-grey-400' },
        { variant: 'solid', color: 'green', className: 'bg-green text-white' },
        { variant: 'solid', color: 'white', className: 'bg-white text-slate-grey' },
        { variant: 'solid', color: 'primary', className: 'bg-primary text-white' },
        { variant: 'solid', color: 'purple', className: 'bg-purple text-white' },
        { variant: 'solid', color: 'red', className: 'bg-red text-white' },
        {
            variant: 'solid',
            color: 'purple-gradient',
            className: `bg-gradient-to-r ${fromPurple} ${toPurple} text-white`,
        },
        {
            variant: 'solid',
            color: 'primary-gradient',
            className: clsx(
                'bg-gradient-to-r from-coral to-orange-500-alt text-white',
                'valar:from-blue-500-alt valar:to-blue-400-alt',
                'blue-frame:from-blue-500-alt blue-frame:to-blue-400-alt',
                `purple-frame:${fromPurple} purple-frame:${toPurple}`,
                'turquoise-frame:from-turquoise-600-alt turquoise-frame:to-turquoise-500-alt',
            ),
        },

        // bordered colors
        {
            color: 'slate-grey',
            variant: 'bordered',
            className: 'border-slate-grey-300 text-slate-grey bg-transparent',
        },
        {
            color: 'slate-grey',
            variant: 'bordered-white',
            className: 'border-slate-grey-300 text-slate-grey bg-white',
        },
        {
            color: 'black',
            variant: 'bordered-white-light',
            className: 'border-slate-grey-200 text-black bg-white',
        },
        {
            color: 'transparent',
            variant: 'bordered',
            className: 'border-white text-white bg-transparent',
        },
        {
            color: 'primary',
            variant: 'bordered',
            className: 'border-primary text-primary',
        },

        // solid-highlight colors
        {
            // When a grey button is highlighted, it converts to a light version of the primary color
            color: 'slate-grey',
            variant: 'solid-highlight',
            className: 'bg-primary-100 text-primary',
        },

        // light colors
        {
            color: 'slate-grey',
            variant: 'light',
            className: 'text-black',
        },
    ],

    variants: {
        // extendVariants only accepts a compoundVariants value that HeroUI ships or that is
        // declared here. The classes for these values live in compoundVariants above, so the
        // entries stay empty.
        color: {
            black: '',
            green: '',
            white: '',
            purple: '',
            red: '',
            'primary-gradient': '',
            'purple-gradient': '',
            'slate-grey': '',
            transparent: '',
        },
        variant: {
            bordered: 'border-1', // HeroUI default is 2px
            'bordered-white': 'border-1',
            'bordered-white-light': 'border-1',
            'solid-highlight': '',
            light: 'data-[hover=true]:bg-default/40', // This is the same thing HeroUI does. I'm not sure what we're doing to lose it and have to add it back in
        },
        radius: {
            full: 'rounded-full',
            2: 'rounded-2',
            5: 'rounded-5',
        },
        size: {
            // We don't use the size variant as a prop, but if we don't override it, the default size:md styling will be applied
            // which can interfere with our styling. The "base" size just takes the values from size:md that we want to keep.
            base: 'px-4 gap-2',
        },
    },
    defaultVariants: {
        size: 'base',
        radius: 5,
    },
});

function getHeightClass({
    height,
    tabletHeight,
    desktopHeight,
}: {
    height: NonNullable<ButtonProps['height']>;
    tabletHeight?: ButtonProps['tabletHeight'];
    desktopHeight?: ButtonProps['desktopHeight'];
}) {
    const heightMap = {
        default: {
            6.25: 'h-6.25',
            8: 'h-8',
            8.5: 'h-8.5',
            9: 'h-9',
            12.5: 'h-12.5',
            18: 'h-18',
        },
        tablet: {
            6.25: 'sm:h-6.25',
            8: 'sm:h-8',
            8.5: 'sm:h-8.5',
            9: 'sm:h-9',
            12.5: 'sm:h-12.5',
            18: 'sm:h-18',
        },
        desktop: {
            6.25: 'md:h-6.25',
            8: 'md:h-8',
            8.5: 'md:h-8.5',
            9: 'md:h-9',
            12.5: 'md:h-12.5',
            18: 'md:h-18',
        },
    };

    const heightClass = heightMap.default[height];

    const tabletHeightClass = tabletHeight ? heightMap.tablet[tabletHeight] : '';
    const desktopHeightClass = desktopHeight ? heightMap.desktop[desktopHeight] : '';

    return `${heightClass} ${tabletHeightClass} ${desktopHeightClass}`.trim();
}

function getWidthClass({
    width,
    tabletWidth,
    desktopWidth,
    isIconOnly,
}: {
    width: ButtonProps['height'];
    tabletWidth?: ButtonProps['tabletHeight'];
    desktopWidth?: ButtonProps['desktopHeight'];
    isIconOnly: boolean;
}) {
    if (!width) return undefined;

    // On small buttons, having the default padding from heroUI sets the icon off by just a bit
    const padding = isIconOnly ? 'px-0' : '';

    const widthMap = {
        default: {
            6.25: 'w-6.25 min-w-6.25',
            8: 'w-8 min-w-8',
            8.5: 'w-8.5 min-w-8.5',
            9: 'w-9 min-w-9',
            12.5: 'w-12.5 min-w-12.5',
            18: 'w-18 min-w-18',
        },
        tablet: {
            6.25: 'sm:w-6.25 sm:min-w-6.25',
            8: 'sm:w-8 sm:min-w-8',
            8.5: 'sm:w-8.5 sm:min-w-8.5',
            9: 'sm:w-9 sm:min-w-9',
            12.5: 'sm:w-12.5 sm:min-w-12.5',
            18: 'sm:w-18 sm:min-w-18',
        },
        desktop: {
            6.25: 'md:w-6.25 md:min-w-6.25',
            8: 'md:w-8 md:min-w-8',
            8.5: 'md:w-8.5 md:min-w-8.5',
            9: 'md:w-9 md:min-w-9',
            12.5: 'md:w-12.5 md:min-w-12.5',
            18: 'md:w-18 md:min-w-18',
        },
    };

    const widthClass = widthMap.default[width];

    const tabletWidthClass = tabletWidth ? widthMap.tablet[tabletWidth] : '';
    const desktopWidthClass = desktopWidth ? widthMap.desktop[desktopWidth] : '';

    return `${padding} ${widthClass} ${tabletWidthClass} ${desktopWidthClass} `.trim();
}

function getFontSizeClass({
    height,
    tabletHeight,
    desktopHeight,
    isIconOnly,
}: {
    height: NonNullable<ButtonProps['height']>;
    tabletHeight: ButtonProps['tabletHeight'];
    desktopHeight: ButtonProps['desktopHeight'];
    isIconOnly: boolean;
}) {
    const fontSizeMap = {
        default: {
            6.25: 'text-3xs',
            8: isIconOnly ? 'text-base' : 'text-xs',
            8.5: 'text-base',
            9: 'text-base',
            12.5: 'text-md',
            18: 'text-md',
        },
        tablet: {
            6.25: 'sm:text-3xs',
            8: isIconOnly ? 'sm:text-base' : 'sm:text-xs',
            8.5: 'sm:text-base',
            9: 'sm:text-base',
            12.5: 'sm:text-md',
            18: 'sm:text-md',
        },
        desktop: {
            6.25: 'md:text-3xs',
            8: isIconOnly ? 'md:text-base' : 'md:text-xs',
            8.5: 'md:text-base',
            9: 'md:text-base',
            12.5: 'md:text-md',
            18: 'md:text-md',
        },
    };

    const fontSizeClass = fontSizeMap.default[height];

    const tabletFontSizeClass = tabletHeight ? fontSizeMap.tablet[tabletHeight] : '';
    const desktopFontSizeClass = desktopHeight ? fontSizeMap.desktop[desktopHeight] : '';

    return `${fontSizeClass} ${tabletFontSizeClass} ${desktopFontSizeClass}`.trim();
}

function getFontWeightClass({ fontWeight }: { fontWeight: ButtonProps['fontWeight'] }) {
    const fontWeightMap = {
        semibold: 'font-semibold',
        normal: 'font-normal',
    };

    return fontWeightMap[fontWeight ?? 'semibold'] ?? 'font-semibold';
}

// Not `FC<ButtonProps>`: that annotation discards the ref this already forwards.
export const Button = memo(
    forwardRef<HTMLButtonElement, ButtonProps>(
        (
            {
                children,
                color = 'slate-grey',
                className,
                variant = 'solid',
                radius,
                height = 12.5,
                tabletHeight,
                desktopHeight,
                round = false,
                fontWeight = 'semibold',
                icon,
                isIconOnly = false,
                ...rest
            },
            ref,
        ) => {
            let width: ButtonProps['height'] | undefined;
            let tabletWidth: ButtonProps['tabletHeight'] | undefined;
            let desktopWidth: ButtonProps['desktopHeight'] | undefined;

            if (round) {
                radius = 'full';
                width = height;
                tabletWidth = tabletHeight;
                desktopWidth = desktopHeight;
            }

            className = twMerge(
                getFontWeightClass({ fontWeight }),
                className,
                getHeightClass({ height, tabletHeight, desktopHeight }),
                getWidthClass({ width, tabletWidth, desktopWidth, isIconOnly }),
                getFontSizeClass({ height, tabletHeight, desktopHeight, isIconOnly }),
            );

            return (
                <ExtendedButton
                    data-id="Button"
                    // extendVariants mistypes its ref as a ReactElement; it forwards the button node.
                    ref={ref as unknown as Ref<ReactElement>}
                    className={className}
                    isIconOnly={isIconOnly}
                    color={color}
                    variant={variant}
                    startContent={icon}
                    radius={radius}
                    {...rest}
                >
                    {children}
                </ExtendedButton>
            );
        },
    ),
);
